import { createEffect, createMemo, onCleanup } from "solid-js";

import type { Channel, Server } from "stoat.js";

import { konkordVoice } from "@revolt/rtc";

export type ServerCall = {
  channel: Channel;
  /** everyone in the channel's call (our voice server and Stoat's) */
  userIds: string[];
  /** someone is streaming their screen */
  streaming: boolean;
};

/**
 * Konkord: calls going on in a server's voice channels, on our voice server
 * (which Stoat doesn't know about) and on Stoat's, kept up to date while used
 */
export function useServerCalls(server: () => Server) {
  // our voice server is only asked about rooms someone looks at
  createEffect(() => {
    const target = server();
    if (!konkordVoice.enabled(target.id)) return;
    for (const channel of target.channels) {
      if (channel.isVoice) onCleanup(konkordVoice.watchRoom(channel.id));
    }
  });

  return createMemo<ServerCall[]>(() =>
    server()
      .channels.filter((channel) => channel.isVoice)
      .map((channel) => {
        const room = konkordVoice.enabled(channel.serverId)
          ? konkordVoice.room(channel.id)
          : undefined;
        const stoat = [...channel.voiceParticipants.values()];
        return {
          channel,
          userIds: [
            ...new Set([
              ...(room?.participants.map((p) => p.id) ?? []),
              ...stoat.map((p) => p.userId),
            ]),
          ],
          streaming:
            !!room?.participants.some((p) => p.streaming) ||
            stoat.some((p) => p.isScreensharing()),
        };
      })
      .filter((call) => call.userIds.length > 0),
  );
}
